import websocketService from '../websocket';

/**
 * Helper function to create a standardized timeout error
 * @param {string} message - The error message
 * @param {string} [code='timeout'] - Error code
 * @param {boolean} [isDataTimeout=true] - Flag to indicate this is a data timeout, not a connection issue
 * @returns {Object} Standardized error object
 */
export const createTimeoutError = (message, code = 'timeout', isDataTimeout = true) => {
  return {
    type: 'error',
    code: code,
    data: message,
    isDataTimeout: isDataTimeout
  };
};

/**
 * Handles a timeout for a WebSocket request
 * @param {string} eventName - The event name to unsubscribe from
 * @param {Function} responseHandler - The handler function to remove
 * @param {string} errorMessage - Error message to display
 * @param {function} rejectFn - The Promise reject function
 */
export const handleRequestTimeout = (eventName, responseHandler, errorMessage, rejectFn) => {
  // Remove the event listener
  websocketService.off(eventName, responseHandler);
  
  // Create and emit a standardized error
  const timeoutError = createTimeoutError(errorMessage);
  websocketService.emit('error', timeoutError);
  
  // Reject the promise
  rejectFn(new Error(errorMessage));
};

/**
 * Create a WebSocket request with proper error handling and retries
 * @param {Object} options - Request options
 * @param {string} options.eventName - Event name for logging
 * @param {string[]} [options.alternativeEventNames] - Alternative event names to listen for
 * @param {string} options.requestType - Type of request to send
 * @param {Object} options.requestData - Additional data to send with request
 * @param {Object} options.requestPayload - New parameter for placing properties at top level
 * @param {Function} options.dataValidator - Function to validate/transform response data
 * @param {string} options.errorMessage - Error message to display if request fails
 * @param {number} options.timeoutMs - Timeout in milliseconds
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {string} [options.requestId] - Custom request ID
 * @returns {Promise<any>} - Promise that resolves with validated data
 */
export const createWebSocketRequest = ({
  eventName,
  alternativeEventNames = [],
  requestType,
  requestData = {},
  requestPayload = null,
  dataValidator = (data) => data,
  errorMessage = 'Request timed out',
  timeout = 10000,
  maxRetries = 2,
  requestId = null
}) => {
  let retryCount = 0;
  
  const executeRequest = () => {
    return new Promise((resolve, reject) => {
      // Check if WebSocket is connected
      const connectionState = websocketService.getConnectionState();
      const isAuthenticated = websocketService.isAuthenticated();
      
      if (connectionState !== 'connected') {
        reject(new Error('WebSocket is not connected'));
        return;
      }
      
      if (!isAuthenticated) {
        reject(new Error('WebSocket is not authenticated'));
        return;
      }
      
      // Create a unique ID for this request or use the provided one
      const actualRequestId = requestId || (Date.now().toString() + Math.random().toString().substring(2, 8));
      
      // Set up timeout
      const timeoutId = setTimeout(() => {
        // Clean up all event listeners
        websocketService.off(eventName, handleResponse);
        alternativeEventNames.forEach(altEvent => {
          websocketService.off(altEvent, handleResponse);
        });
        
        reject(new Error(`${errorMessage || 'Request timed out'}`));
      }, timeout);
      
      // Handler for response
      const handleResponse = (data) => {
        clearTimeout(timeoutId);
        
        // Clean up all event listeners
        websocketService.off(eventName, handleResponse);
        alternativeEventNames.forEach(altEvent => {
          websocketService.off(altEvent, handleResponse);
        });
        
        try {
          const validatedData = dataValidator(data);
          if (validatedData !== undefined) {
            resolve(validatedData);
          } else {
            reject(new Error('Invalid response data'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      // Listen for primary event name
      websocketService.on(eventName, handleResponse);
      
      // Also listen for alternative event names if provided
      alternativeEventNames.forEach(altEvent => {
        websocketService.on(altEvent, handleResponse);
      });
      
      // Create the request payload
      let requestPayloadFinal;
      
      if (requestPayload) {
        // If requestPayload is provided, use it for top-level properties
        requestPayloadFinal = {
          type: requestType,
          request_id: actualRequestId,
          ...requestPayload
        };
      } else {
        // Otherwise use the traditional format with nested requestData
        requestPayloadFinal = {
          type: requestType,
          request_id: actualRequestId,
          ...requestData
        };
      }
      
      console.log(`Sending WebSocket request: ${requestType}`, requestPayloadFinal);
      
      // Log all event names we're listening for
      console.log(`Listening for response events: primary=${eventName}, alternatives=${alternativeEventNames.join(', ') || 'none'}`);
      
      websocketService.send(requestPayloadFinal);
    });
  };
  
  // Retry function with exponential backoff
  const executeWithRetry = async () => {
    try {
      return await executeRequest();
    } catch (error) {
      if (retryCount < maxRetries) {
        retryCount++;
        const delay = 1000 * Math.pow(2, retryCount - 1); // Exponential backoff
        
        console.log(`Retrying request (${retryCount}/${maxRetries}) after ${delay}ms delay...`);
        
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(executeWithRetry());
          }, delay);
        });
      }
      throw error;
    }
  };
  
  return executeWithRetry();
}; 